/**
 * Dialpad Slice
 * Manages the pre-call dialpad state: digits, cursor position, and active flag.
 * The DOM text-field is bound via registerDialpadTextField / getDialpadTextField
 * (closure-held, never serialised) so that grid + input components share one node.
 * Serialisable state (dialpadDigits, cursorPosition, isDialpadActive) lives in Zustand.
 */
import { INVALID_DIALPAD_INPUT_CHARACTERS } from 'Modules/softphone/components/constants';
import { StateCreator } from 'zustand';
import { SoftphoneStore } from '../..';
import { DialpadSlice, DialpadSliceState } from './index.types';

const initialState: DialpadSliceState = {
  isDialpadActive: false,
  dialpadDigits: '',
  cursorPosition: null,
  cursorSelectionEnd: null,
};

export const createDialpadSlice: StateCreator<
  SoftphoneStore,
  [],
  [],
  DialpadSlice
> = (set, get) => {
  let dialpadTextField: HTMLInputElement | null = null;

  return {
    ...initialState,

    setDialpadDigits: (digits) => {
      set({ dialpadDigits: digits });
    },

    /** Clears the field while the dialpad may stay open — cursor at 0 (start). */
    clearDialpadDigits: () => {
      set({ dialpadDigits: '', cursorPosition: 0, cursorSelectionEnd: 0 });
    },

    setDialpadActive: (active) => {
      // When opening dialpad, close keypad (mutual exclusion)
      if (active && get().isKeypadActive) {
        set({ isKeypadActive: false, keypadDigits: '' });
      }

      set({ isDialpadActive: active });

      if (!active) {
        dialpadTextField = null;
        /* Closing UI: no active field — cursor null (vs clearDialpadDigits → 0 while open). */
        set({ dialpadDigits: '', cursorPosition: null, cursorSelectionEnd: null });
      }
    },

    setCursorPosition: (position) => {
      set({ cursorPosition: position });
    },

    setCursorSelectionEnd: (position) => {
      set({ cursorSelectionEnd: position });
    },

    registerDialpadTextField: (element) => {
      dialpadTextField = element;
    },

    getDialpadTextField: () => dialpadTextField,

    handleDialpadDigitPress: (digit: string) => {
      const filtered = digit.replace(INVALID_DIALPAD_INPUT_CHARACTERS, '');
      if (!filtered) {
        return;
      }

      const { dialpadDigits, cursorPosition, cursorSelectionEnd } = get();
      const input = get().getDialpadTextField();

      if (!input) {
        set({ dialpadDigits: dialpadDigits + filtered });
        return;
      }

      const len = dialpadDigits.length;
      const clampedStored =
        cursorPosition !== null
          ? Math.min(Math.max(0, cursorPosition), len)
          : null;
      const clampedStoredEnd =
        cursorSelectionEnd !== null
          ? Math.min(Math.max(0, cursorSelectionEnd), len)
          : null;

      const inputIsFocused =
        typeof document !== 'undefined' && document.activeElement === input;

      let start: number;
      let end: number;

      if (inputIsFocused) {
        const domStart = input.selectionStart;
        const domEnd = input.selectionEnd;
        start =
          domStart !== null && domStart !== undefined
            ? domStart
            : (clampedStored ?? len);
        end =
          domEnd !== null && domEnd !== undefined ? domEnd : (clampedStoredEnd ?? clampedStored ?? len);
        start = Math.min(Math.max(0, start), len);
        end = Math.min(Math.max(0, end), len);
      } else {
        start = clampedStored ?? len;
        end = clampedStoredEnd ?? start;
      }

      const newValue =
        dialpadDigits.substring(0, start) +
        filtered +
        dialpadDigits.substring(end);
      const newCursorPos = start + filtered.length;
      set({ dialpadDigits: newValue, cursorPosition: newCursorPos, cursorSelectionEnd: newCursorPos });
    },
  };
};
